import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Search, X } from 'lucide-react';
import useStore from '../../store/useStore';
import useSchoolId from '../../hooks/useSchoolId';
import { supabase } from '../../supabase';
import { DESTINATIONS, canSee } from '../../utils/navCatalog';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const BAR_W = 360;
const PER_GROUP = 5;

const CSS = `
.gs-bar{display:flex;align-items:center;height:34px;box-sizing:border-box;border-radius:8px;border:1px solid var(--primary-200);background:rgba(255,255,255,.75);overflow:hidden;transition:width .22s ease,background .15s ease;}
.gs-bar.open{background:#fff;border-color:var(--primary);}
.gs-icon{width:32px;height:32px;flex:none;border:none;background:transparent;color:var(--primary);display:inline-flex;align-items:center;justify-content:center;cursor:pointer;}
.gs-input{flex:1;min-width:0;height:100%;border:none;outline:none;background:transparent;font-size:13px;color:var(--text-primary);padding:0 4px 0 0;}
.gs-kbd{flex:none;font-size:10px;color:var(--text-hint);border:1px solid var(--border);border-radius:5px;padding:1px 5px;margin-right:6px;}
.gs-clear{width:26px;height:26px;flex:none;margin-right:3px;border:none;border-radius:6px;background:transparent;color:var(--text-hint);display:inline-flex;align-items:center;justify-content:center;cursor:pointer;}
.gs-clear:hover{background:var(--primary-50);}
.gs-panel{position:absolute;top:42px;right:0;width:${BAR_W}px;max-height:min(70vh,520px);overflow-y:auto;background:#fff;border:1.5px solid var(--primary-200);border-radius:14px;box-shadow:0 12px 32px rgba(15,23,42,.18);z-index:400;padding:4px 0;}
.gs-group{padding:8px 14px 4px;font-size:10px;font-weight:700;letter-spacing:.08em;text-transform:uppercase;color:var(--text-hint);}
.gs-item{display:flex;align-items:center;gap:10px;width:100%;box-sizing:border-box;padding:7px 14px;border:none;background:transparent;text-align:left;cursor:pointer;color:var(--text-primary);}
.gs-item.active,.gs-item:hover{background:var(--primary-50);}
.gs-main{flex:1;min-width:0;font-size:13px;font-weight:600;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
.gs-sub{flex:none;max-width:45%;font-size:11px;color:var(--text-hint);white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
.gs-empty{padding:14px;font-size:12.5px;color:var(--text-hint);}
@media (prefers-reduced-motion:reduce){.gs-bar{transition:none;}}
`;

// The one search in the app. Everything it can open is filtered by the user's permissions.
export default function GlobalSearch({ open: openProp, onOpenChange }) {
  const { students, setPage, hasPermission } = useStore();
  const schoolId = useSchoolId();
  // The Topbar owns which menu is open; standalone use falls back to local state.
  const [openState, setOpenState] = useState(false);
  const controlled = typeof openProp === 'boolean';
  const open = controlled ? openProp : openState;
  const setOpen = useCallback((v) => {
    if (controlled) { if (onOpenChange) onOpenChange(v); } else setOpenState(v);
  }, [controlled, onOpenChange]);
  const [q, setQ] = useState('');
  const [active, setActive] = useState(0);
  const [challans, setChallans] = useState([]);
  const wrapRef = useRef(null);
  const inputRef = useRef(null);
  const reqRef = useRef(0);

  const needle = q.trim().toLowerCase();

  const close = useCallback(() => { setOpen(false); }, [setOpen]);

  // Ctrl+K / Cmd+K opens it from anywhere
  useEffect(() => {
    const h = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') { e.preventDefault(); setOpen(true); }
    };
    document.addEventListener('keydown', h);
    return () => document.removeEventListener('keydown', h);
  }, [setOpen]);

  useEffect(() => { if (open && inputRef.current) inputRef.current.focus(); }, [open]);
  // However it was closed (Escape, outside click, another menu opening), start clean next time
  useEffect(() => { if (!open) { setQ(''); setActive(0); setChallans([]); } }, [open]);

  // Escape collapses it (outside clicks are handled once, in the Topbar)
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') close(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, close]);

  // Challans are looked up live, only when the text looks like a challan number
  const looksLikeChallan = /^chn/i.test(needle) || (needle.match(/\d/g) || []).length >= 3;
  const canSearchChallans = hasPermission('fees.view');
  useEffect(() => {
    if (!open || !schoolId || !canSearchChallans || !looksLikeChallan) { setChallans([]); return undefined; }
    const id = ++reqRef.current;
    const timer = setTimeout(async () => {
      const safe = needle.replace(/[%_,()]/g, '');
      if (!safe) return;
      const { data, error } = await supabase
        .from('challans')
        .select('id, challan_no, month, year, families(family_id_display, father_name)')
        .eq('school_id', schoolId)
        .ilike('challan_no', `%${safe}%`)
        .limit(PER_GROUP);
      if (id !== reqRef.current) return;
      setChallans(error ? [] : (data || []));
    }, 250);
    return () => clearTimeout(timer);
  }, [open, needle, schoolId, canSearchChallans, looksLikeChallan]);

  const groups = useMemo(() => {
    if (!needle) return [];
    const words = needle.split(/\s+/);
    const rank = (label) => { const l = label.toLowerCase(); return l.startsWith(needle) ? 0 : l.includes(needle) ? 1 : 2; };
    const matchDest = (d) => {
      const hay = [d.label, d.parent || '', ...d.keywords].join(' ').toLowerCase();
      return words.every(w => hay.includes(w));
    };
    const visible = DESTINATIONS.filter(d => canSee(d, hasPermission) && matchDest(d));
    const toItem = (d) => ({
      key: d.id,
      main: d.label,
      sub: d.kind === 'page' ? 'Page' : d.parent,
      go: () => setPage(d.page, d.action),
    });
    const out = [];
    const pages = visible.filter(d => d.kind !== 'settings').sort((a, b) => rank(a.label) - rank(b.label)).slice(0, PER_GROUP);
    if (pages.length) out.push({ title: 'Pages & modules', items: pages.map(toItem) });
    const settings = visible.filter(d => d.kind === 'settings').sort((a, b) => rank(a.label) - rank(b.label)).slice(0, PER_GROUP);
    if (settings.length) out.push({ title: 'Settings', items: settings.map(toItem) });

    if (needle.length >= 2) {
      const list = students || [];
      if (hasPermission('students.view')) {
        const hits = list.filter(s =>
          (s.name || '').toLowerCase().includes(needle) ||
          (s.admissionNo || '').toLowerCase().includes(needle) ||
          (s.familyId || '').toLowerCase().includes(needle)
        ).slice(0, PER_GROUP);
        if (hits.length) {
          out.push({
            title: 'Students',
            items: hits.map(s => ({
              key: `stu-${s.id}`,
              main: s.name || '—',
              sub: [s.class ? `${s.class}${s.section ? `-${s.section}` : ''}` : '', s.familyId].filter(Boolean).join(' · '),
              go: () => setPage('students', { type: 'open_profile', studentId: s.id }),
            })),
          });
        }
      }
      if (hasPermission('families.view')) {
        const byFamily = new Map();
        list.forEach(s => {
          if (!s.familyId) return;
          const f = byFamily.get(s.familyId) || { familyId: s.familyId, father: s.fatherName || '', count: 0 };
          f.count += 1;
          if (!f.father && s.fatherName) f.father = s.fatherName;
          byFamily.set(s.familyId, f);
        });
        const hits = [...byFamily.values()].filter(f =>
          f.familyId.toLowerCase().includes(needle) || f.father.toLowerCase().includes(needle)
        ).slice(0, PER_GROUP);
        if (hits.length) {
          out.push({
            title: 'Families',
            items: hits.map(f => ({
              key: `fam-${f.familyId}`,
              main: f.father || f.familyId,
              sub: `${f.familyId} · ${f.count} ${f.count === 1 ? 'student' : 'students'}`,
              go: () => setPage('families'),
            })),
          });
        }
      }
      // Staff is not loaded into the store, so it is not searched here.
    }

    if (challans.length) {
      out.push({
        title: 'Challans',
        items: challans.map(c => ({
          key: `chn-${c.id}`,
          main: c.challan_no,
          sub: [c.families?.father_name || c.families?.family_id_display, c.month ? `${MONTHS[c.month - 1] || ''} ${c.year}` : ''].filter(Boolean).join(' · '),
          go: () => setPage('fees', { type: 'find_student', studentName: c.families?.family_id_display || c.challan_no }),
        })),
      });
    }
    return out;
  }, [needle, students, challans, hasPermission, setPage]);

  const flat = useMemo(() => groups.flatMap(g => g.items), [groups]);

  useEffect(() => { setActive(0); }, [needle]);
  useEffect(() => {
    const el = document.getElementById(`gs-opt-${active}`);
    if (el && el.scrollIntoView) el.scrollIntoView({ block: 'nearest' });
  }, [active]);

  const choose = (item) => { if (!item) return; close(); item.go(); };

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); if (flat.length) setActive(i => (i + 1) % flat.length); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); if (flat.length) setActive(i => (i - 1 + flat.length) % flat.length); }
    else if (e.key === 'Enter') { e.preventDefault(); choose(flat[active]); }
  };

  const showPanel = open && needle.length > 0;
  let flatIndex = -1;

  return (
    <div ref={wrapRef} data-menu="search" style={{ position: 'relative', flexShrink: 0 }}>
      <style>{CSS}</style>
      <div className={`gs-bar${open ? ' open' : ''}`} style={{ width: open ? BAR_W : 34 }}>
        <button type="button" className="gs-icon" aria-label="Search" title="Search (Ctrl+K)" aria-expanded={open} aria-haspopup="listbox" onClick={() => setOpen(!open)}>
          <Search size={16} aria-hidden="true" />
        </button>
        {open && (
          <>
            <input
              ref={inputRef}
              className="gs-input"
              type="text"
              value={q}
              placeholder="Search pages, students, families, challans"
              aria-label="Search"
              role="combobox"
              aria-expanded={showPanel}
              aria-controls="gs-list"
              aria-activedescendant={flat.length ? `gs-opt-${active}` : undefined}
              autoComplete="off"
              onChange={e => setQ(e.target.value)}
              onKeyDown={onKeyDown}
            />
            {q ? (
              <button type="button" className="gs-clear" aria-label="Clear search" onClick={() => { setQ(''); if (inputRef.current) inputRef.current.focus(); }}>
                <X size={14} aria-hidden="true" />
              </button>
            ) : (
              <span className="gs-kbd" aria-hidden="true">Esc</span>
            )}
          </>
        )}
      </div>

      {showPanel && (
        <div className="gs-panel" id="gs-list" role="listbox" aria-label="Search results">
          {flat.length === 0 ? (
            <div className="gs-empty">{`No results for "${q.trim()}"`}</div>
          ) : groups.map(g => (
            <div key={g.title}>
              <div className="gs-group">{g.title}</div>
              {g.items.map(item => {
                flatIndex += 1;
                const idx = flatIndex;
                return (
                  <button
                    key={item.key}
                    type="button"
                    id={`gs-opt-${idx}`}
                    role="option"
                    aria-selected={idx === active}
                    tabIndex={-1}
                    className={`gs-item${idx === active ? ' active' : ''}`}
                    onMouseEnter={() => setActive(idx)}
                    onClick={() => choose(item)}
                  >
                    <span className="gs-main">{item.main}</span>
                    {item.sub && <span className="gs-sub">{item.sub}</span>}
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
